import React from 'react';
import {
  Box, Container, Grid, Typography, Card, CardContent,
  Stack, Chip, useTheme, Paper,
  Fade, Grow, Slide, Zoom, alpha
} from '@mui/material';
import HomeFooter from '../HomeFooter';
import Navbar from '../Navbar.jsx';
import { styled, keyframes } from '@mui/material/styles';
import {
  CloudQueue, CloudUpload, DataUsage, Storage, Analytics,
  Security, Speed, Code, TrendingUp,
  CheckCircle, GroupWork, Bolt,
  Polyline, Hub, NetworkPing, Terminal,
  AutoGraph, ModelTraining, Dashboard,
  Verified, SupportAgent,
  RocketLaunch, WorkspacePremium, Shield
} from '@mui/icons-material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';

// Professional color palette (Same as other pages)
const colors = {
  primary: '#4551be', // Exact blue
  secondary: '#6b77d4', // Lighter blue accent
  success: '#10b981', // Green
  warning: '#f59e0b', // Orange
  dark: '#1e293b', // Dark text
  light: '#f0f4f8', // Light background
  gray: '#64748b', // Slate 500
  accent: '#4551be',
  textSecondary: '#64748b', // Dark grey for secondary text
  gradient: 'linear-gradient(135deg, #4551be 0%, #6b77d4 100%)'
};

// Animations
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
`;

const ServiceCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: `0 20px 40px -12px ${alpha(colors.primary, 0.2)}`
  }
}));

const FeatureItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(1.5)
}));

const CloudDataAnalyticsCyber = () => {
  const theme = useTheme();

  // Why Choose Us Data
  const whyChooseUs = [
    {
      icon: <Verified />,
      title: "Expertise",
      points: ["10+ years cloud experience", "500+ successful migrations", "Certified cloud architects"]
    },
    {
      icon: <GroupWork />,
      title: "Collaboration",
      points: ["Partnership approach", "Strategic alignment", "Dedicated support team"]
    },
    {
      icon: <RocketLaunch />,
      title: "Innovation",
      points: ["Cutting-edge technology", "AI-driven solutions", "Continuous optimization"]
    },
    {
      icon: <SupportAgent />,
      title: "Support",
      points: ["24/7 proactive monitoring", "Rapid issue resolution", "Regular performance reviews"]
    },
    {
      icon: <Shield />,
      title: "Security",
      points: ["Enterprise-grade protection", "Compliance certified", "Data encryption"]
    },
    {
      icon: <WorkspacePremium />,
      title: "Quality",
      points: ["100% success rate", "Guaranteed SLAs", "Best practices"]
    }
  ];

  // Cloud Services
  const cloudServices = [
    {
      icon: <CloudUpload />,
      title: 'Cloud Migration',
      description: 'Seamless migration with zero downtime and minimal disruption to your operations.',
      color: colors.primary,
      features: ['Zero-downtime', 'Data Integrity', 'Post-migration Support']
    },
    {
      icon: <Terminal />,
      title: 'Infrastructure Setup',
      description: 'Automated cloud infrastructure deployment with Infrastructure as Code practices.',
      color: colors.secondary,
      features: ['IaC Implementation', 'Auto-scaling', 'High Availability']
    },
    {
      icon: <Code />,
      title: 'Cloud Development',
      description: 'Native cloud applications built for scalability and optimal performance.',
      color: colors.success,
      features: ['Microservices', 'Serverless', 'Containerization']
    },
    {
      icon: <Security />,
      title: 'Security & Compliance',
      description: 'Enterprise-grade security with compliance certifications and continuous monitoring.',
      color: colors.warning,
      features: ['Encryption', 'Access Control', 'Compliance Audits']
    },
    {
      icon: <Speed />,
      title: 'Cost Optimization',
      description: 'AI-driven cost management to optimize your cloud spending and resource usage.',
      color: colors.primary,
      features: ['Cost Analysis', 'Resource Optimization', 'Budget Alerts']
    },
    {
      icon: <Hub />,
      title: 'Multi-Cloud Strategy',
      description: 'Strategic multi-cloud solutions integrating AWS, Azure and Google Cloud.',
      color: colors.secondary,
      features: ['Vendor Neutral', 'Hybrid Solutions', 'Interoperability']
    }
  ];

  // Data Analytics Services
  const dataServices = [
    {
      icon: <Storage />,
      title: 'Data Warehousing',
      description: 'Scalable data architecture for efficient storage and retrieval of enterprise data.',
      color: colors.primary,
      features: ['Data Lakes', 'ETL Pipelines', 'Real-time Processing']
    },
    {
      icon: <Dashboard />,
      title: 'Business Intelligence',
      description: 'Interactive dashboards and reports for data-driven decision making.',
      color: colors.secondary,
      features: ['KPI Tracking', 'Custom Reports', 'Data Visualization']
    },
    {
      icon: <AutoGraph />,
      title: 'Data Visualization',
      description: 'Compelling visual representations of complex data for better insights.',
      color: colors.success,
      features: ['Interactive Charts', 'Geospatial Analysis', 'Real-time Graphs']
    },
    {
      icon: <ModelTraining />,
      title: 'Predictive Analytics',
      description: 'ML-powered forecasting to predict trends and optimize business outcomes.',
      color: colors.warning,
      features: ['Forecasting', 'Pattern Recognition', 'Anomaly Detection']
    },
    {
      icon: <Polyline />,
      title: 'Big Data Processing',
      description: 'Handle massive datasets with Hadoop, Spark and distributed computing.',
      color: colors.primary,
      features: ['Batch Processing', 'Stream Processing', 'Data Mining']
    },
    {
      icon: <NetworkPing />,
      title: 'Real-time Analytics',
      description: 'Streaming data pipelines for immediate insights and rapid response.',
      color: colors.secondary,
      features: ['Stream Processing', 'Event-driven', 'Low Latency']
    }
  ];

  // Tech Stack
  const techStack = [
    { name: 'AWS', icon: '☁️', color: '#FF9900' },
    { name: 'Azure', icon: '🔵', color: '#0078D4' },
    { name: 'Google Cloud', icon: '🌐', color: '#4285F4' },
    { name: 'Apache Spark', icon: '⚡', color: '#E25A1C' },
    { name: 'Kubernetes', icon: '⎈', color: '#326CE5' },
    { name: 'TensorFlow', icon: '🧠', color: '#FF6F00' },
    { name: 'Snowflake', icon: '❄️', color: '#29B5E8' },
    { name: 'Kafka', icon: '📊', color: '#231F20' },
    { name: 'Databricks', icon: '🔷', color: '#FF3621' },
    { name: 'Tableau', icon: '📈', color: '#E97627' }
  ];

  const process = [
    { step: "01", title: "Discovery", description: "We analyze your infrastructure and requirements" },
    { step: "02", title: "Strategy", description: "We design a tailored cloud migration strategy" },
    { step: "03", title: "Migration", description: "We execute the migration with zero downtime" },
    { step: "04", title: "Optimization", description: "We optimize performance and costs" },
    { step: "05", title: "Monitoring", description: "24/7 monitoring and proactive maintenance" },
    { step: "06", title: "Evolution", description: "Continuous improvement and scaling" }
  ];

  return (
    <Box sx={{ bgcolor: colors.light }}>
      <Navbar />

      {/* Hero Section */}
            <Box
  sx={{
    minHeight: { xs: 'auto', md: '60vh' },
    display: 'flex',
    alignItems: 'center',

    // Navbar overlap fix
   pt: { xs: 9, sm: 7, md: 8 },
    pb: { xs: 7, sm: 9, md: 12 },

    background: `linear-gradient(
      135deg,
      ${alpha(colors.primary, 0.03)} 0%,
      ${alpha(colors.secondary, 0.03)} 100%
    )`,

    position: 'relative',
    overflow: 'hidden',
    width: '100%',
  }}
>
        <Container maxWidth="xl">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Fade in timeout={1000}>
                <Box>
                  <Chip
                    icon={<CloudQueue />}
                    label="☁️ CLOUD & DATA ANALYTICS"
                    sx={{
                      mb: 3,
                      mt: { xs: 2, md: 6 },
                      bgcolor: alpha(colors.primary, 0.1),
                      color: colors.primary,
                      fontWeight: 600,
                      border: `1px solid ${alpha(colors.primary, 0.2)}`
                    }}
                  />

                  <Typography variant="h1" sx={{
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    fontWeight: 800,
                    lineHeight: 1.2,
                    mb: 3,
                    color: colors.dark // Dark text
                  }}>
                    Transform Your
                    <Box component="span" sx={{
                      display: 'block',
                      color: colors.primary // Blue text
                    }}>
                      Digital Infrastructure
                    </Box>
                    with Advanced Data Intelligence
                  </Typography>

                  <Typography sx={{
                    color: colors.textSecondary, // Dark grey text
                    mb: 4,
                    fontSize: '1.2rem',
                    lineHeight: 1.7,
                    maxWidth: '90%'
                  }}>
                    We deliver cutting-edge cloud solutions and data analytics services that
                    propel businesses into the future. Experience unmatched performance,
                    scalability and insights.
                  </Typography>
                </Box>
              </Fade>
            </Grid>

            <Grid item xs={12} md={6}>
              <Zoom in timeout={1000} style={{ transitionDelay: '300ms' }}>
                <Box sx={{
                  animation: `${float} 6s ease-in-out infinite`,
                  textAlign: 'center',
                  position: 'relative'
                }}>
                  <Box sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: { xs: '280px', md: '380px' },
                    height: { xs: '280px', md: '380px' },
                    borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
                    background: `linear-gradient(135deg, ${alpha(colors.primary, 0.1)} 0%, ${alpha(colors.secondary, 0.1)} 100%)`,
                    margin: '0 auto',
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: -15,
                      left: -15,
                      right: -15,
                      bottom: -15,
                      borderRadius: '40% 60% 60% 40% / 40% 40% 60% 60%',
                      background: `linear-gradient(135deg, ${alpha(colors.primary, 0.2)} 0%, ${alpha(colors.secondary, 0.2)} 100%)`,
                      zIndex: -1,
                      animation: `${float} 8s ease-in-out infinite reverse`
                    }
                  }}>
                    <CloudQueue sx={{
                      fontSize: { xs: 100, md: 150 },
                      color: colors.primary
                    }} />
                  </Box>

                  {/* Floating Elements */}
                  <Box sx={{
                    position: 'absolute',
                    top: '15%',
                    right: '10%',
                    width: 60,
                    height: 60,
                    borderRadius: '15px',
                    background: alpha(colors.success, 0.2),
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center',
                    justifyContent: 'center',
                    animation: `${float} 7s ease-in-out infinite`,
                    animationDelay: '1s'
                  }}>
                    <Storage sx={{ color: colors.success }} />
                  </Box>

                  <Box sx={{
                    position: 'absolute',
                    bottom: '20%',
                    left: '5%',
                    width: 50,
                    height: 50,
                    borderRadius: '10px',
                    background: alpha(colors.warning, 0.2),
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center',
                    justifyContent: 'center',
                    animation: `${float} 8s ease-in-out infinite`,
                    animationDelay: '2s'
                  }}>
                    <Analytics sx={{ color: colors.warning }} />
                  </Box>

                  <Box sx={{
                    position: 'absolute',
                    top: '30%',
                    left: '15%',
                    width: 45,
                    height: 45,
                    borderRadius: '50%',
                    background: alpha(colors.secondary, 0.2),
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center',
                    justifyContent: 'center',
                    animation: `${float} 9s ease-in-out infinite`,
                    animationDelay: '1.5s'
                  }}>
                    <DataUsage sx={{ color: colors.secondary, fontSize: 25 }} />
                  </Box>
                </Box>
              </Zoom>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Why Choose Us Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box textAlign="center" mb={6}>
          <Chip
            label="WHY CHOOSE US"
            sx={{
              bgcolor: alpha(colors.primary, 0.1),
              color: colors.primary,
              fontWeight: 600,
              mb: 2
            }}
          />
          <Typography variant="h2" sx={{ fontWeight: 700, mb: 2, color: colors.dark }}>
            Why Leading Companies Trust Us
          </Typography>
          <Typography variant="h5" sx={{ maxWidth: 700, mx: 'auto', color: colors.textSecondary }}>
            We deliver excellence through expertise, innovation and unwavering commitment to quality
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {whyChooseUs.map((item, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Paper sx={{
                p: 3,
                height: '100%',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 24px ${alpha(colors.primary, 0.15)}`
                }
              }}>
                <Box sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  background: colors.gradient,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                  color: 'white'
                }}>
                  {item.icon}
                </Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: colors.dark }}>
                  {item.title}
                </Typography>
                <Stack spacing={1}>
                  {item.points.map((point, idx) => (
                    <FeatureItem key={idx}>
                      <CheckCircle sx={{ fontSize: 16, color: colors.success }} />
                      <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                        {point}
                      </Typography>
                    </FeatureItem>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Process Section */}
      <Box sx={{ bgcolor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <Chip
              label="OUR PROCESS"
              sx={{
                bgcolor: alpha(colors.primary, 0.1),
                color: colors.primary,
                fontWeight: 600,
                mb: 2
              }}
            />
            <Typography variant="h2" sx={{ fontWeight: 700, mb: 2, color: colors.dark }}>
              How We Transform Your Cloud
            </Typography>
            <Typography variant="h5" sx={{ maxWidth: 700, mx: 'auto', color: colors.textSecondary }}>
              A proven methodology for successful cloud migration and optimization
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {process.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Paper sx={{
                  p: 3,
                  height: '100%',
                  position: 'relative',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 24px ${alpha(colors.primary, 0.15)}`
                  }
                }}>
                  <Typography variant="h2" sx={{
                    position: 'absolute',
                    top: 10,
                    right: 20,
                    fontSize: '4rem',
                    fontWeight: 900,
                    color: alpha(colors.primary, 0.1),
                    lineHeight: 1
                  }}>
                    {item.step}
                  </Typography>
                  <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: colors.primary }}>
                      {item.title}
                    </Typography>
                    <Typography sx={{ color: colors.textSecondary }}>
                      {item.description}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Cloud Services */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box textAlign="center" mb={6}>
          <Chip
            label="CLOUD SERVICES"
            sx={{
              bgcolor: alpha(colors.primary, 0.1),
              color: colors.primary,
              fontWeight: 600,
              mb: 2
            }}
          />
          <Typography variant="h2" sx={{ fontWeight: 700, mb: 2, color: colors.dark }}>
            Cloud Services Ecosystem
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {cloudServices.map((service, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <ServiceCard>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{
                    width: 50,
                    height: 50,
                    borderRadius: 2,
                    bgcolor: alpha(service.color, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                    color: service.color
                  }}>
                    {service.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: colors.dark }}>
                    {service.title}
                  </Typography>
                  <Typography variant="body2" paragraph sx={{ color: colors.textSecondary }}>
                    {service.description}
                  </Typography>
                  <Stack spacing={1}>
                    {service.features.map((feature, idx) => (
                      <FeatureItem key={idx}>
                        <CheckCircle sx={{ fontSize: 14, color: colors.success }} />
                        <Typography variant="caption" sx={{ color: colors.dark }}>{feature}</Typography>
                      </FeatureItem>
                    ))}
                  </Stack>
                </CardContent>
              </ServiceCard>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Data Analytics Services */}
      <Box sx={{ bgcolor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <Chip
              label="DATA ANALYTICS"
              sx={{
                bgcolor: alpha(colors.primary, 0.1),
                color: colors.primary,
                fontWeight: 600,
                mb: 2
              }}
            />
            <Typography variant="h2" sx={{ fontWeight: 700, mb: 2, color: colors.dark }}>
              Data Intelligence Platform
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {dataServices.map((service, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <ServiceCard>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{
                      width: 50,
                      height: 50,
                      borderRadius: 2,
                      bgcolor: alpha(service.color, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                      color: service.color
                    }}>
                      {service.icon}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: colors.dark }}>
                      {service.title}
                    </Typography>
                    <Typography variant="body2" paragraph sx={{ color: colors.textSecondary }}>
                      {service.description}
                    </Typography>
                    <Stack spacing={1}>
                      {service.features.map((feature, idx) => (
                        <FeatureItem key={idx}>
                          <CheckCircle sx={{ fontSize: 14, color: colors.success }} />
                          <Typography variant="caption" sx={{ color: colors.dark }}>{feature}</Typography>
                        </FeatureItem>
                      ))}
                    </Stack>
                  </CardContent>
                </ServiceCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Technology Stack */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box textAlign="center" mb={6}>
          <Chip
            label="TECHNOLOGY STACK"
            sx={{
              bgcolor: alpha(colors.primary, 0.1),
              color: colors.primary,
              fontWeight: 600,
              mb: 2
            }}
          />
          <Typography variant="h2" sx={{ fontWeight: 700, mb: 2, color: colors.dark }}>
            Modern Tools We Use
          </Typography>
        </Box>

        <Box sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: 2
        }}>
          {techStack.map((tech, index) => (
            <Chip
              key={index}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>{tech.icon}</span>
                  <span>{tech.name}</span>
                </Box>
              }
              sx={{
                px: 2,
                py: 3,
                fontSize: '1rem',
                bgcolor: 'white',
                boxShadow: 1,
                border: `1px solid ${alpha(tech.color, 0.3)}`,
                color: colors.dark, // Dark text
                '&:hover': {
                  bgcolor: alpha(tech.color, 0.1),
                  cursor: 'pointer'
                }
              }}
            />
          ))}
        </Box>
      </Container>

      {/* CTA Section */}
      <Box
        sx={{
          background: colors.gradient,
          py: { xs: 6, md: 8 },
          px: 2,
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: "center" }}>
          <Typography
            variant="h3"
            gutterBottom
            sx={{
              fontWeight: 700,
              color: "#ffffff",
              fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
            }}
          >
            Ready for Digital Transformation?
          </Typography>

          <Typography
            variant="h6"
            sx={{
              color: alpha("#ffffff", 0.9),
              mb: 4,
              fontSize: { xs: "1rem", md: "1.25rem" },
              lineHeight: 1.6,
            }}
          >
            Join 500+ businesses that have transformed their cloud
            infrastructure with us.
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "center",
              alignItems: "center",
              gap: { xs: 2, sm: 4 },
              mt: 4,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <EmailIcon
                sx={{
                  color: "#ffffff",
                  fontSize: { xs: 20, md: 24 },
                }}
              />

              <Typography
                sx={{
                  color: "#ffffff",
                  fontSize: { xs: "0.9rem", md: "1rem" },
                  wordBreak: "break-word",
                  textAlign: "center",
                }}
              >
                lidertechnologysolutions@gmail.com
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
            >
              <PhoneIcon
                sx={{
                  color: "#ffffff",
                  fontSize: { xs: 20, md: 24 },
                }}
              />

              <Typography
                sx={{
                  color: "#ffffff",
                  fontSize: { xs: "0.9rem", md: "1rem" },
                }}
              >
                +91 98403 69993
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      <HomeFooter />
    </Box>
  );
};

export default CloudDataAnalyticsCyber;